import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
// Realtor 탭용 io5 아이콘
import { IoHome, IoHomeOutline, IoThumbsUp, IoThumbsUpOutline, IoList, IoListOutline, IoPerson, IoPersonOutline } from 'react-icons/io5';
// Partner 탭용 md 아이콘 (기존 유지)
import { MdDashboard, MdStorage, MdPayment, MdAccountBalance, MdPerson } from 'react-icons/md';

const ICON_MAP = {
  // io5 (Realtor)
  IoHome, IoHomeOutline, IoThumbsUp, IoThumbsUpOutline, IoList, IoListOutline, IoPerson, IoPersonOutline,
  // md (Partner)
  MdDashboard, MdStorage, MdPayment, MdAccountBalance, MdPerson,
};

// active일 때 filled, 아닐 때 outline 아이콘 매핑
const OUTLINE_MAP = {
  IoHome: 'IoHomeOutline',
  IoThumbsUp: 'IoThumbsUpOutline',
  IoList: 'IoListOutline',
  IoPerson: 'IoPersonOutline',
};

const Wrapper = styled.nav`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  height: ${({ theme }) => theme.realtor?.bottomNavHeight || '60px'};
  background: #fff;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: none;
  /* z-index 200 — 외부 앱(카카오공유 등) 다녀온 후 stale 모달/오버레이가 BottomNav 위로 덮어 클릭 막는 케이스 회피 */
  z-index: 200;
  pointer-events: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.realtorMobile}) {
    display: flex;
  }
`;

const Tab = styled(NavLink)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textLight};
  transition: color 0.15s;
  pointer-events: auto;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  touch-action: manipulation;

  &.active {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const BottomNav = ({ tabs }) => {
  // 페이지 전환 / 외부 앱 복귀 시 모바일 WebView 의 fixed positioning 이 한 박자 잘못 잡혀
  // BottomNav 클릭 영역이 어긋나는 문제 — 스크롤 한 번 올리면 reflow 되어 정상화
  // 동일 효과를 강제: location/visibility 변동 시 컴포넌트 key 토글 + 강제 reflow
  const [tick, setTick] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    const bump = () => {
      setTick((t) => t + 1);
      // 강제 reflow — fixed positioning 재계산
      try {
        // eslint-disable-next-line no-unused-expressions
        document.body && document.body.offsetHeight;
        window.scrollBy(0, 1);
        window.scrollBy(0, -1);
      } catch (_) { /* skip */ }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') bump();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', bump);
    window.addEventListener('focus', bump);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', bump);
      window.removeEventListener('focus', bump);
    };
  }, []);

  // 페이지 전환 직후 reflow 강제 + key 토글
  useEffect(() => {
    setTick((t) => t + 1);
    const id = requestAnimationFrame(() => {
      try {
        // eslint-disable-next-line no-unused-expressions
        document.body && document.body.offsetHeight;
        window.scrollBy(0, 1);
        window.scrollBy(0, -1);
      } catch (_) { /* skip */ }
    });
    return () => cancelAnimationFrame(id);
  }, [pathname]);

  return (
    <Wrapper key={tick}>
      {tabs.map((tab) => (
        <Tab key={tab.key} to={tab.path} end={tab.path === '/realtor'}>
          {({ isActive }) => {
            const iconName = isActive ? tab.icon : (OUTLINE_MAP[tab.icon] || tab.icon);
            const Icon = ICON_MAP[iconName];
            return (
              <>
                {Icon && <Icon size={22} />}
                {tab.label}
              </>
            );
          }}
        </Tab>
      ))}
    </Wrapper>
  );
};

export default BottomNav;
